import { motion } from "framer-motion";
import { Thermometer, MapPin } from "lucide-react";

const WINDOWS = [30, 40, 50, 60, 70, 80, 90];

export default function SoilTempTool({ idPrefix = "soil-temp", data = null, loading = false, error = "", hasLocation = false }) {
  const temp = data?.soil_2in_f;
  const pct = temp != null ? Math.max(0, Math.min(100, ((temp - 30) / 60) * 100)) : 0;

  const prompt = loading
    ? "Reading live soil temperature…"
    : "Search a location above to read live soil temperature and your pre-emergent window.";

  return (
    <div
      data-testid={`${idPrefix}-tool`}
      className="rounded-3xl border border-[var(--ld-line)] bg-[var(--ld-surface)]/80 p-6 backdrop-blur-xl sm:p-7"
    >
      <div className="mb-4 flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.22em] text-[var(--ld-lime)]">
        <Thermometer className="h-3.5 w-3.5" /> Live Soil · 2 in
      </div>

      <div className="flex items-end gap-2">
        <span
          data-testid={`${idPrefix}-value`}
          className="ld-display text-6xl sm:text-7xl"
          style={{ color: temp != null ? "var(--ld-amber)" : "var(--ld-muted)" }}
        >
          {temp != null ? `${temp}°` : "—"}
        </span>
        {data && (
          <span className="mb-2 flex items-center gap-1 text-sm font-bold text-[var(--ld-muted)]">
            <MapPin className="h-3.5 w-3.5" /> {data.place}{data.state ? `, ${data.state}` : ""}
          </span>
        )}
      </div>

      <p className="mt-2 min-h-[2.5rem] text-sm font-semibold leading-snug text-[var(--ld-text)]" data-testid={`${idPrefix}-status`}>
        {data ? data.status : prompt}
      </p>

      {/* Meter */}
      <div className="mt-4">
        <div className="relative h-2 w-full rounded-full bg-[var(--ld-surface-2)]">
          <div className="absolute inset-y-0 left-[33%] w-[8%] rounded-full bg-[var(--ld-lime)]/40" title="Pre-emergent window" />
          {temp != null && (
            <motion.div
              initial={{ left: 0 }}
              animate={{ left: `${pct}%` }}
              transition={{ type: "spring", stiffness: 120, damping: 18 }}
              className="absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-[var(--ld-bg)] bg-[var(--ld-amber)] shadow-[0_0_0_4px_rgba(230,161,92,0.25)]"
            />
          )}
        </div>
        <div className="mt-1.5 flex justify-between text-[10px] font-bold text-[var(--ld-muted)]">
          {WINDOWS.map((w) => (
            <span key={w}>{w}°</span>
          ))}
        </div>
      </div>

      {error && (
        <p className="mt-4 text-xs font-bold text-red-400" data-testid={`${idPrefix}-error`}>
          {error}
        </p>
      )}
      {data && (
        <div className="mt-4 grid grid-cols-3 gap-2 border-t border-[var(--ld-line)] pt-4 text-center">
          <Stat label="Surface" value={data.surface_f} />
          <Stat label="2 in" value={data.soil_2in_f} highlight />
          <Stat label="6 in" value={data.soil_6in_f} />
        </div>
      )}
    </div>
  );
}

const Stat = ({ label, value, highlight }) => (
  <div>
    <div className={`ld-display text-2xl ${highlight ? "text-[var(--ld-amber)]" : "text-[var(--ld-text)]"}`}>
      {value != null ? `${value}°` : "—"}
    </div>
    <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--ld-muted)]">{label}</div>
  </div>
);
